import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfPoint, PdfBrush, PdfColor } from '../../pdf/pdf';
import { ILayoutImageGen } from '../layoutImage';
import { IItemCount } from '@educraft/base';
import { OptsImage } from '../optsImage';
import { GUtil } from '../../pdf/gutil';
import { GLabel } from '../../pdf/glabel';
import '../../pdf/draw/primitives';
import '../../pdf/draw/image';
import '../../pdf/draw/label';

/**
 * Tetris - Tetris-style grid with colored image blocks
 *
 * Layout: 3 portrait pages.
 * Page 1: colored background + images. Page 2: labels. Page 3: empty grid.
 *
 * Migrated from C# EduFrame.Img10.Generators.Tetris
 */
export class Tetris implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 10;
  }
  countMax(): number {
    return 10;
  }
  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const margin = PdfUnit.FromMillimeters(10);
    const countX = 10;
    const sizeWidth = (PdfSize.A4.width - 2 * margin) / countX;
    const countY = Math.floor((PdfSize.A4.height - 2 * margin) / sizeWidth);

    const rect = new PdfRect(
      margin,
      (PdfSize.A4.height - sizeWidth * countY) / 2,
      sizeWidth * 10,
      sizeWidth * countY
    );

    interface U {
      index: number;
      color: PdfColor;
    }

    const predefined: U[][] = [
      [
        { index: 0, color: PdfColor.fromHex('#FFA500') },
        { index: 0, color: PdfColor.fromHex('#FFA500') },
        { index: 0, color: PdfColor.fromHex('#FFA500') },
        { index: 1, color: PdfColor.fromHex('#FF0000') },
        { index: 1, color: PdfColor.fromHex('#FF0000') },
        { index: 2, color: PdfColor.fromHex('#00FFFF') },
        { index: 2, color: PdfColor.fromHex('#00FFFF') },
        { index: 2, color: PdfColor.fromHex('#00FFFF') },
        { index: 2, color: PdfColor.fromHex('#00FFFF') },
        { index: 2, color: PdfColor.fromHex('#00FFFF') },
      ],
      [
        { index: 0, color: PdfColor.fromHex('#FFA500') },
        { index: 3, color: PdfColor.fromHex('#FFC0CB') },
        { index: 3, color: PdfColor.fromHex('#FFC0CB') },
        { index: 3, color: PdfColor.fromHex('#FFC0CB') },
        { index: 1, color: PdfColor.fromHex('#FF0000') },
        { index: 1, color: PdfColor.fromHex('#FF0000') },
        { index: 4, color: PdfColor.fromHex('#FFFF00') },
        { index: 4, color: PdfColor.fromHex('#FFFF00') },
        { index: 5, color: PdfColor.fromHex('#0000FF') },
        { index: 2, color: PdfColor.fromHex('#00FFFF') },
      ],
      [
        { index: 4, color: PdfColor.fromHex('#FFFF00') },
        { index: 4, color: PdfColor.fromHex('#FFFF00') },
        { index: 3, color: PdfColor.fromHex('#FFC0CB') },
        { index: 6, color: PdfColor.fromHex('#008000') },
        { index: 6, color: PdfColor.fromHex('#008000') },
        { index: 3, color: PdfColor.fromHex('#FFC0CB') },
        { index: 4, color: PdfColor.fromHex('#FFFF00') },
        { index: 4, color: PdfColor.fromHex('#FFFF00') },
        { index: 5, color: PdfColor.fromHex('#0000FF') },
        { index: 2, color: PdfColor.fromHex('#00FFFF') },
      ],
      [
        { index: 4, color: PdfColor.fromHex('#FFFF00') },
        { index: 4, color: PdfColor.fromHex('#FFFF00') },
        { index: 6, color: PdfColor.fromHex('#008000') },
        { index: 6, color: PdfColor.fromHex('#008000') },
        { index: 3, color: PdfColor.fromHex('#FFC0CB') },
        { index: 3, color: PdfColor.fromHex('#FFC0CB') },
        { index: 3, color: PdfColor.fromHex('#FFC0CB') },
        { index: 5, color: PdfColor.fromHex('#0000FF') },
        { index: 5, color: PdfColor.fromHex('#0000FF') },
        { index: 2, color: PdfColor.fromHex('#00FFFF') },
      ],
    ];

    const page1 = pdf.addPage();
    const page2 = pdf.addPage();
    const page3 = pdf.addPage();

    // Compute shared font for all labels on page 2
    const allLabelTexts = opts.list.map((item) => [item.label]);
    const labelFont =
      allLabelTexts.length > 0
        ? page2.adjustFontMulti(allLabelTexts, GUtil.baseFont, new PdfSize(sizeWidth, sizeWidth))
        : GUtil.baseFont;

    for (let p = 0; p < Math.floor(countY / predefined.length); p++) {
      for (let y = 0; y < predefined.length; y++) {
        for (let x = 0; x < countX; x++) {
          const y1 = p * predefined.length + y;
          const r = new PdfRect(
            rect.left + x * sizeWidth,
            rect.top + y1 * sizeWidth,
            sizeWidth,
            sizeWidth
          );
          const u = predefined[y][x];

          if (u.index < opts.list.length) {
            // Page 1: colored bg + image
            page1.drawRectangle(null, new PdfBrush(u.color), r);
            page1.drawImageCenter(opts.list[u.index].image, r);
            page1.drawRectangle(GUtil.basePen, null, r);

            // Page 2: label text with shared font
            page2.drawLabel(GLabel.fromText(opts.list[u.index].label), {
              rect: r,
              font: labelFont,
            });
            page2.drawRectangle(GUtil.basePen, null, r);
          }
        }
      }
    }

    // Page 3: empty grid
    for (let y = 0; y < countY; y++) {
      for (let x = 0; x < countX; x++) {
        const r = new PdfRect(
          rect.left + x * sizeWidth,
          rect.top + y * sizeWidth,
          sizeWidth,
          sizeWidth
        );
        page3.drawRectangle(GUtil.basePen, null, r);
      }
    }
  }
}
